// actions.js contains Vuex actions
// https://vuex.vuejs.org/guide/actions.html
// Actions do not mutate states directly, Actions commit mutations
// Actions can be asynchronous

import axios from "axios";
import JSZip from "jszip";
import moment from "moment";
import router from "../../router";
import { debouncePerKey, logDetailed } from "../../helpers/utils";
import { formatDateDisplay, formatDateISO } from "../../helpers/dateUtils";
import { setupRefreshTokenLoop, setupAutologoutEvents } from "../../helpers/idle";
import { setupRefreshPatientLoop, disableRefreshPatientLoop } from "../../helpers/patientRefresh";
import { saveBlob } from "../../helpers/blobsaver";
import { onSiteInitializedInApp } from "../../helpers/appInterface";
import { GRAYSCALE_MODES, SliceLayerTypes, EyeTypes, SEARCH_BY, PATIENT_TIMESPAN, 
  THUMBNAIL_DROPDOWN, VALID_IMG_TYPES, DEVICE_HISTORY_EDITABLE_FIELDS
 } from "../../config/config";
import CanvasRenderer from "../../canvas/canvasRenderer";
import {
  getAnnotationType, drawShadedImage, applyPixelShadingRules, hasCustomPixelColorSettings
} from "../../canvas/canvasUtils";
import {
  generateContextForImage, getManufacturer, generateContextFromExam,
  generateContextFromGroupName, generateContextFromGroupAndExSrNo, 
  generateContextFromEye, generateMultiviewFromContext,
  generateOCTContextFromGroup, generateMultiviewFromEye
} from "../../imageBrowserContext/contextFactory";
import HvfMultiviewContext from "../../imageBrowserContext/hvfMultiviewContext";
import HvfContext from "../../imageBrowserContext/hvfContext";
import ImageContext from "../../imageBrowserContext/imageContext";
import StereoContext from "../../imageBrowserContext/StereoContext";
import SideBySideContext from "../../imageBrowserContext/SideBySideContext";
import PrintPreviewContext from "../../imageBrowserContext/PrintPreviewContext";
import { requestPrintInApp } from "../../imageBrowserContext/print";
import { imageAlreadyLoaded } from "./helpers";
import { logoutAndReject } from "./authActions";

// #region get static data - site info, physicians, etc.
export const initialize = ({ commit, state, dispatch, getters }) => {
  logDetailed("actions", "initialize", "initializing ui site");
  let initial = sessionStorage.getItem("store");
  let json = JSON.parse(initial);
  if (initial && json) commit("initialize", json);

  let storeAuth = sessionStorage.getItem("auth");
  let jsonAuth = JSON.parse(storeAuth);
  if (storeAuth && jsonAuth) commit("initialize", jsonAuth);
  
  if (state.auth) commit("updateAuth", state.auth);

  let startupTask = dispatch("getSiteInfo");
  
  if (getters.isAuthenticated)
    startupTask = startupTask
      .then(() => dispatch("isTokenExpired"))
      .then(() => {
        dispatch("getUserPrefs");
        setupAutologoutEvents();
        setupRefreshTokenLoop();
      }) 
      .catch(error => {
        logDetailed("actions", "initialize", "post failed");
        return logoutAndReject(dispatch, error);
      });

  startupTask.finally(onSiteInitializedInApp);

  return startupTask;
};
export const getSiteInfo = ({ commit }) => {
  logDetailed("actions", "getSiteInfo");
  return axios.get("/api/siteinfo")
    .then(response => {
      logDetailed("actions", "getSiteInfo", "post succeeded", response.data);
      if (response.data.defaultPatientListing != null) {
        response.data.defaultPatientListing = response.data.defaultPatientListing.toLowerCase();
        // for discrepancy value in between db vs patient list dropdown
        if(response.data.defaultPatientListing == "today") response.data.defaultPatientListing = "day";
        commit("setPatientDateSetting", response.data.defaultPatientListing);
      }
      if (response.data.autoRefreshPatientList != null)
        commit("setAutoPatientRefresh", response.data.autoRefreshPatientList ? 1 : 0);
      commit("siteInfo", response.data);
      return Promise.resolve(response);
    })
    .catch(error => {
      logDetailed("actions", "getSiteInfo", "post failed");
      return Promise.reject(error.response);
    });
}
export const getActDirEnabled = ({ commit }) =>
  axios.get("/api/siteinfo/actDirEnabled")
    .then(response => {
      var isEnabled = response.data == "false" ? false : true;
      commit("setActDirEnabled", isEnabled);
      return Promise.resolve(response);
    })
    .catch(error => Promise.reject(error.response));
export const getPhysicians = ({ commit, getters }, forceLoad) => {
  let doLoad = forceLoad || !getters.physicians || !getters.physicians.length;
  if (doLoad)
    return axios.get("/api/physician/list")
      .then(response => {
        const physicians = response.data.data.map(value => {
          return {
            phyStatus: value.phyStatus,
            phyLevel: value.phyLevel,
            phyUsrSrNo: value.phyUsrSrNo + "",
            phySrNo: value.phySrNo + "",
            lastName: value.phyLName,
            firstName: value.phyFName,
            name: value.phyName,
            isSelected: value.isSelected,
            modalityList: value.phyModalityList
          };
        });
        commit("setPhysicians", physicians);
      })
      .catch(error => Promise.reject(error.response));
  return Promise.resolve();
};
export const getDevices = ({ commit, getters }, forceLoad) => {
  let doLoad = forceLoad || !getters.devices || !getters.devices.length;
  if (doLoad)
    return axios.get("/api/device/userDeviceList")
      .then(response => {
        response.data.data.forEach(device => {
          if (device.configuration && window.DOMParser) {
            let doc = new DOMParser().parseFromString(device.configuration, "text/xml");
            let key = doc.children ? "children" : "childNodes";
            let nodes = doc[key];
            if (nodes && nodes.length && nodes[0][key] && nodes[0][key].length) {
              let data = Array.from(nodes[0][key], n => {
                return {
                  type: n.attributes.TYPE.value,
                  value: n.attributes.VALUE.value,
                  desc: n.attributes.DESC.value
                };
              });
              device.configs = data;
            }
          }
          if (device.defaultZoomFactor)
            device.defaultZoomFactor = parseFloat(device.defaultZoomFactor).toFixed(2);
          let description = device.description.toLowerCase();
          device.manufacturer = getManufacturer(description);
          device.isOptos = description.includes("optos");
          device.isOCT = description.includes("oct");
          device.isScans = description.includes("scans");
          device.devSrNo = device.srNo + "";
          device.isActive = device.isActive == true;
        });
        commit("setDevices", response.data.data);
      })
      .catch(error => Promise.reject(error.response));
  return Promise.resolve();
};
export const getdevLocations = ({ commit, getters }, forceLoad) => {
  let doLoad = forceLoad || !getters.devLocations || !getters.devLocations.length;
  if (doLoad)
    return axios.get("/api/device/devLocations")
      .then(response => {
        const devLocationsList = response.data.map(x => {
          return {
            value: x,
            text: x
          };
        });
        commit("setdevLocations", devLocationsList);
      })
      .catch(error => Promise.reject(error.response));
  return Promise.resolve();
};
export const getProcedures = ({ commit, getters }, forceLoad) => {
  let doLoad = forceLoad || !getters.procedures || !getters.procedures.length;
  if (doLoad)
    return axios.get("/api/procedure/list")
      .then(response => {
        const procedures = response.data.map(value => {
          return {
            dpSrNo: value.dpSrNo + "",
            dpName: value.dpName,
          };
        });
        commit("setProcedures", procedures);
      })
      .catch(error => Promise.reject(error.response));
  return Promise.resolve();
};

export const getPracticeGroupsList = ({ commit }, ) => {
    return axios.get("/api/siteinfo/getPracticeGroupsList")
      .then(response => {
        commit("setPracticeGroupsList", response.data.data);
      })
      .catch(error => Promise.reject(error.response));
};

export const ensureStaticListsLoaded = ({ dispatch }, forceLoad) => Promise.all([
  dispatch("getPhysicians", forceLoad),
  dispatch("getDevices", forceLoad),
  dispatch("getdevLocations", forceLoad),
  dispatch("getProcedures", forceLoad),
  dispatch("getActDirEnabled"),
  dispatch("getPracticeGroupsList")
]);
// #endregion

// #region administrator actions
export const setAxisSettings = ({ commit, dispatch }, payload) => {
  commit("setLoadingMessage", "Updating...");
  axios.post("/api/siteinfo/updateAxisSettings", {
    DefaultPatientListing: payload.patientDefaultListing,
    UseDefaultHvf: payload.useDefaultHVF,
    OverrideMultiviewHVFCheck: payload.overrideMultiviewHVFCheck,
    UserInactivityTimeLimit: payload.userInactivityTimeLimit,
    IsActDirEnabled: payload.actDirEnabled,
    LDAPPathList: payload.actDirPath,
    GroupAdmin: payload.actDirGroupAxisAdmin,
    GroupAttdMD: payload.actDirGroupAxisAttndMD,
    GroupRefMD: payload.actDirGroupAxisRefMD,
    GroupUser: payload.actDirGroupAxisUser,
    GroupReadOnly: payload.actDirGroupAxisReadOnly,
    GroupViewOnly: payload.actDirGroupAxisViewOnly,
    GroupPhotographer: payload.actDirGroupAxisPhotographer,
    SiteDefaultHVFview: payload.siteDefaultHVFview,
    CirrusScanDisplayRatio : payload.cirrusScanDisplayRatio,
    UserLockoutEnabled: payload.userLockoutEnabled,
    UserFailedLoginAttempts: payload.userFailedLoginAttempts,
    UserLockoutDuration: payload.userLockoutDuration,
    UserFailedLoginAttemptsReset: payload.userFailedLoginAttemptsReset
  })
    .then(response => {
      if (!response.data) return Promise.reject();
      dispatch("getSiteInfo");
      dispatch("getActDirEnabled");
      commit("displayStatus", { message: "Success!", level: "success" });
      return Promise.resolve(response);
    }).catch(() => {
      commit("displayStatus", { message: "An error occurred while updating configurations. Please try again later.", level: "error" });
      return Promise.reject()
    }).finally(() => commit("setLoadingMessage", null));
};
export const getActDir = () =>
  axios.get("/api/siteinfo/getActDir")
    .then(response => {
      return Promise.resolve(response);
    });

export const getUserList = ({ commit }) =>
  axios.post("/api/result/getuserlist")
    .then(response => {
      response.data.data.forEach(user => {
        // todo - these mappings don't belong here
        // data shouldn't have to match display
        if (user.usrStat.toUpperCase() == "ACTIVE") user.status = "Active";
        else user.status = "Inactive";
        switch (user.desig) {
          case 0: user.desig = "Admin"; break;
          case 1: user.desig = "AttdMD"; break;
          case 2: user.desig = "RefMD"; break;
          case 3: user.desig = "Photographer"; break;
          case 4: user.desig = "User"; break;
          case 5: user.desig = "ReadOnly"; break;
          case 6: user.desig = "ADUser"; break;
          case 7: user.desig = "RCSiteUser"; break;
          case 8: user.desig = "Invalid"; break;
          case 9: user.desig = "Device"; break;
          case 10: user.desig = "Reviewer"; break;
          case 11: user.desig = "ViewOnly"; break;
        }
      });
      commit("userList", response.data.data);
      return Promise.resolve(response);
    })
    .catch(error => Promise.reject(error.response));

export const statsList = (context, payload) =>
  axios.post("/api/result/statslistdays", payload)
    .then(response => {
      response.data.forEach(user => {
        // todo - these mappings don't belong here
        // data shouldn't have to match display
        user.month = mapMonth(user.month);
      });
      return Promise.resolve(response);
    });


export const statsFilesList = (context, payload) =>
  axios.post("/api/result/statsfileslistdays", payload)
    .then(response => {
      response.data.forEach(user => {
        // todo - these mappings don't belong here
        // data shouldn't have to match display
        user.month = mapMonth(user.month);
      });
      return Promise.resolve(response);
    });

function mapMonth(number) {
  switch (number) {
    case 1: return "January";
    case 2: return "February";
    case 3: return "March";
    case 4: return "April";
    case 5: return "May";
    case 6: return "June";
    case 7: return "July";
    case 8: return "August";
    case 9: return "September";
    case 10: return "October";
    case 11: return "November";
    default: return "December";
  }
}

export const processUser = (context, payload) =>
  axios.post("/api/account/userprocess", {
    ID: payload.id,
    Pass: payload.password,
    FName: payload.fName,
    LName: payload.lName,
    Desig: payload.desig,
    IsValid: true,
    IsActive: payload.isActive,
    Stat: payload.isActive ? 0 : 1,
    ChangePWD: payload.changePWD,
    ChangePWDMust: payload.changePWDMust,
    UserPrefMenu: payload.userPrefMenu,
    ContactNumber: payload.contactNumber,
    Email: payload.email,
    SrNo: payload.srNo,
    act: payload.act,
    Dept: payload.dept,
    user: null,
    PracticeGroupIds: payload.practiceGroupId
  });

export const processPhysician = (context, payload) => {
  if (payload.associatedUser[0].name == " ") {
    return axios.post("/api/physician/physicianprocess", {
      SrNo: payload.srNo,
      UsrSrNo: payload.associatedUser[0].srNo,
      FName: payload.fName,
      LName: payload.lName,
      Name: payload.name,
      PhyLevel: payload.phyLevel,
      phyModalityList: payload.modalityList,
      PhyStatus: payload.phyStatus,
      IsSelected: payload.isSelected,
      act: payload.act,
      associatedUser: null
    });
  }
  else {
    return axios.post("/api/physician/physicianprocess", {
      SrNo: payload.srNo,
      UsrSrNo: payload.associatedUser[0].srNo,
      FName: payload.fName,
      LName: payload.lName,
      Name: payload.name,
      PhyLevel: payload.phyLevel,
      phyModalityList: payload.modalityList,
      PhyStatus: payload.phyStatus,
      IsSelected: payload.isSelected,
      act: payload.act,
      associatedUser: {
        ID: payload.associatedUser[0].id,
        SrNo: payload.associatedUser[0].srNo,
        usrSrNo: payload.associatedUser[0].usrSrNo,
        FName: payload.associatedUser[0].fName,
        LName: payload.associatedUser[0].lName,
        Dept: payload.associatedUser[0].dept,
        Desig: payload.associatedUser[0].desig,
        Stat: payload.associatedUser[0].stat,
        IsValid: payload.associatedUser[0].isValid,
        IsActive: payload.associatedUser[0].isActive,
        ChangePWD: payload.associatedUser[0].changePWD,
        ChangePWDMust: payload.associatedUser[0].changePWDMust,
        UserPrefMenu: payload.associatedUser[0].userPrefMenu,
        DOB: payload.associatedUser[0].dob,
        RCAssociatedSiteSrNo: payload.associatedUser[0].rcAssociatedSiteSrNo,
        RCVFSPermit: payload.associatedUser[0].rcvfsPermit,
        RCOCTPermit: payload.associatedUser[0].rcoctPermit,
        RCDesig: payload.associatedUser[0].rcDesig,
        ContactNumber: payload.associatedUser[0].contactNumber,
        Email: payload.associatedUser[0].email,
        usrMFA: payload.associatedUser[0].usrMFA
      }
    });
  }
}

export const processDevice = (context, payload) =>
  axios.post("/api/device/deviceprocess", {
        devSrNo: payload.devSrNo,
        devType: payload.devType,
        devName: payload.devName,
        devProc: payload.devProc,
        devDescrition: payload.devDescrition,
        isActive: payload.isActive,
        ignoreRotation: payload.ignoreRotation,
        orderByLaterality: payload.orderByLaterality,
        defaultZoomFactor: payload.defaultZoomFactor,
        devConfig: payload.devConfig
  }).then(response => {
    return Promise.resolve(response);
  });

export const getDeviceLogs = ({commit}, payload) => {
  return axios.post("/api/device/devicelogs", {
    devSrNo: payload.devSrNo,
    sortBy: payload.sortBy,
    itemsPerPage: payload.itemsPerPage,
    page: payload.page,
    sortDesc: payload.sortDesc
  }).then(response => {
    commit("setDeviceLogs", response.data.data);
    return Promise.resolve(response);
  })
  .catch(error => Promise.reject(error.response));
}
// #endregion

// #region user preferences
export const getUserPrefs = ({ getters, commit }) =>
  axios.post("/api/account/getuserpref")
    .then(response => {
      let userPrefs = response.data.data;
      if (userPrefs) {
        commit("setUserPrefs", userPrefs);
        if (userPrefs.patientTimespan) commit("setSearchTimespan", userPrefs.patientTimespan);
        else commit("setSearchTimespan", getters.defaultPatientListing);
        if (userPrefs.autoPatientExamRefresh) setupRefreshPatientLoop();
        else disableRefreshPatientLoop();
        if (((userPrefs.fullscreen && router.currentRoute.path == "/login" && !getters.isFullscreen) || (userPrefs.fullscreen == 0 && getters.isFullscreen)) && !getters.calledFullscreenToggle) {
          commit("setCalledFullscreenToggle");
          setTimeout(() => {
            commit("toggleFullscreen");
            router.push("/");
          }, 500);
        }
      }
      else commit("setSearchTimespan", getters.defaultPatientListing);
    })
    .catch(error => Promise.reject(error.response));
export const setUserPrefs = ({ commit }, payload) =>
  axios.post("/api/account/setuserpref", {
    fullscreen: payload.fullscreen,
    patientTimespan: payload.patientDateSetting,
    defaultColorTheme: payload.defaultColorTheme,
    pageSize: payload.pageSize,
    autoPatientExamRefresh: payload.autoPatientExamRefresh,
    defaultHVF: payload.defaultHVF,
    defaultCirrusView: payload.defaultCirrusView,
    defaultHVFview: payload.defaultHVFview,
    multiviewCollapse: payload.multiviewCollapse,
    cirrusScanDisplayRatio: payload.cirrusScanDisplayRatio
  }).then(() => {
    var userPrefs = {
      usrSrNo: payload.SrNo,
      fullscreen: payload.fullscreen,
      patientTimespan: payload.patientDateSetting,
      defaultColorTheme: payload.defaultColorTheme,
      pageSize: payload.pageSize,
      autoPatientExamRefresh: payload.autoPatientExamRefresh,
      defaultHVF: payload.defaultHVF,
      defaultCirrusView: payload.defaultCirrusView,
      defaultHVFview: payload.defaultHVFview,
      multiviewCollapse: payload.multiviewCollapse,
      cirrusScanDisplayRatio: payload.cirrusScanDisplayRatio
    };
    commit("setUserPrefs", userPrefs);
  }).catch(error => Promise.reject(error.response));

export const loginChangePass = (context, payload) =>
  axios.post("/api/account/loginpasschange", payload)
    .then(response => Promise.resolve(response.data))
    .catch(error => Promise.reject(error.response));
// #endregion

// #region edit patients/exams
export const removePatientExam = (context, payload) =>
  axios
    .delete("/api/exam/" + payload)
    .catch(error => Promise.reject(error.response));
export const submitEditExam = ({ getters, dispatch }) => {
  logDetailed("actions", "submitEditExam");
  let editToExam = getters.examToEdit;
  if (editToExam)
    dispatch("editExam").then(() => {
      logDetailed("actions", "submitEditExam", "getting patients and patient exams");
      dispatch("getPatients")
        .finally(() => dispatch("getPatientExams"));
      return Promise.resolve();
    })

  return Promise.resolve();
};
export const generateExamZip = ({ commit, getters }, exam) => {
  let patient = getters.patient;
  if (!exam || !patient) return;
  let request = {
    ptSrNo: patient.ptSrNo,
    lastName: patient.lastName,
    firstName: patient.firstName,
    dob: patient.dob,
    exSrNo: [exam.exSrNo],
    exDate: [exam.exDate],
    exDevice: [exam.exDevice]
  };
  commit("setLoadingMessage", "Generating zip file");
  return axios.post("/api/exam/generateZip", request)
    .catch(error => {
      commit("displayStatus", { message: "An error occurred while generating the zip file, please try again later.", level: "error" });
      return Promise.reject(error);
    })
    .then(response => commit("setExamToDownload", response.data))
    .finally(() => commit("setLoadingMessage", null));
};
export const generateExamZipForChecked = ({ commit, getters }) => {
  let patient = getters.patient;
  let selectedExams = getters.selectedExams;
  if (selectedExams.length == 0 || !selectedExams)
    selectedExams = getters.exams;
  if (!selectedExams || !patient) return;
  let exSrNoList = [];
  let exDateList = [];
  let exDeviceList = [];
  selectedExams.forEach(e => {
    exSrNoList.push(e.exSrNo);
    exDateList.push(e.exDate);
    exDeviceList.push(e.exDevice);
  });
  let request = {
    ptSrNo: patient.ptSrNo,
    lastName: patient.lastName,
    firstName: patient.firstName,
    dob: patient.dob,
    exSrNo: exSrNoList,
    exDate: exDateList,
    exDevice: exDeviceList
  };
  commit("setLoadingMessage", "Generating zip file");
  axios.post("/api/exam/generateZip", request)
    .catch(error => {
      commit("displayStatus", { message: "An error occurred while generating the zip file, please try again later.", level: "error" });
      return Promise.reject(error);
    })
    .then(response => commit("setExamToDownload", response.data))
    .finally(() => commit("setLoadingMessage", null));
};
export const downloadExamZip = ({ commit, dispatch, getters }) => {
  if (!getters.examToDownload || !getters.examToDownload.retObject) return;
  let fileName = getters.examToDownload.retObject;
  let escaped = encodeURIComponent(getters.examToDownload.retObject);
  commit("setLoadingMessage", "Downloading zip file");
  axios.get("api/exam/zip/?fileName=" + escaped, {
    responseType: "arraybuffer"
  }).then(response => {
    let blob = new Blob([response.data], { type: "application/zip" });
    return dispatch("downloadBlob", { blob, fileName });
  }).catch(() => {
    commit("displayStatus", { message: "An error occurred while downloading the zip file, please try again later.", level: "error" });
  }).finally(() => {
    commit("setLoadingMessage", null);
  });
};
export const downloadBlob = (context, { blob, fileName }) => {
  saveBlob(blob, fileName);
};
export const addPatient = (context, patient) =>
  axios.post("/api/patient", {
    PtId: patient.ptId,
    LastName: patient.lastName,
    FirstName: patient.firstName,
    DateOfBirth: patient.dob,
    Gender: patient.gender
  }).catch(error => Promise.reject(error.response));
export const editPatient = (context, patient) =>
  axios.put("/api/patient", {
    PtSrNo: patient.ptSrNo,
    PtId: patient.ptId,
    LastName: patient.lastName,
    FirstName: patient.firstName,
    DateOfBirth: patient.dob,
    Gender: patient.gender,
    ValidityStatus: patient.status
  }).catch(error => Promise.reject(error.response));
export const saveExam = (context, patient) => {
  let eyes = [];
  let eyeString = "";
  for (let i = 0; i < patient.files.length; i++) {
    if (!eyes.includes(patient.files[i].eye))
      eyes.push(patient.files[i].eye);
  }
  for (let j = 0; j < eyes.length; j++) {
    if (j == eyes.length - 1)
      eyeString = eyeString.concat(eyes[j]);
    else
      eyeString = eyeString.concat(eyes[j] + ", ");
  }
  return axios.post("/api/exam", {
    PtSrNo: patient.ptSrNo,
    ExSrNo: patient.exam.exSrNo,
    DeviceSrNo: patient.exam.type,
    Date: patient.exam.date,
    PhysicianSrNo: patient.exam.physician,
    Eye: eyeString
  }).catch(error => Promise.reject(error.response));
};
export const editExam = ({ dispatch }) => Promise.all([
  dispatch("editExamPhysician"),
  dispatch("editExamDiagnosis"),
  dispatch("editExamDate"),
  dispatch("editExamDictation"),
  dispatch("editExamTeachingCase"),
  dispatch("editExamImagesInfo")
]);
export const editExamPhysician = ({ getters, commit }) => {
  return axios.post("/api/exam/editPhysician", {
    ExSrNo: getters.examToEdit.exSrNo,
    Date: getters.examToEdit.dateIso,
    ExAttdMDList: getters.examToEdit.exAttdMD.map(Number),
    ExRefMDList: getters.examToEdit.exRefMD.map(Number),
    ExDevice: getters.examToEdit.exDevice
  }).catch(error => {
    commit("displayStatus", { message: "Error saving exam physician data.", level: "error" });
    return Promise.reject(error.response);
  });
};
export const editExamDiagnosis = ({ getters, commit }) => {
  return axios.post("/api/exam/editDiagnosis", {
    ExSrNo: getters.examToEdit.exSrNo,
    Date: getters.examToEdit.dateIso,
    PrimaryDX: getters.examToEdit.exDxPrimary,
    SecondaryDX: getters.examToEdit.exDXSecondary,
    Pathology: getters.examToEdit.exPathology,
    ExDevice: getters.examToEdit.exDevice
  }).catch(error => {
    commit("displayStatus", { message: "Error saving exam diagnosis data.", level: "error" });
    return Promise.reject(error.response);
  });
};
export const editExamDate = ({ getters, commit }) => {
  axios.post("/api/exam/editExamDate", {
    ExSrNo: getters.examToEdit.exSrNo,
    Date: getters.examToEdit.dateIso,
    ExDevice: getters.examToEdit.exDevice
  }).catch(error => {
    commit("displayStatus", { message: "Error saving exam date data.", level: "error" });
    return Promise.reject(error.response);
  });
};
export const editExamDictation = ({ getters, commit }) => {
  axios.post("/api/exam/editDictation", {
    ExSrNo: getters.examToEdit.exSrNo,
    ExDict: getters.examToEdit.exDict,
  }).catch(error => {
    commit("displayStatus", { message: "Error saving exam dictation data.", level: "error" });
    return Promise.reject(error.response);
  });
};
export const editExamTeachingCase = ({ getters, commit }) => {
  axios.post("/api/exam/editTeachingCase", {
    ExSrNo: getters.examToEdit.exSrNo,
    ExIsStudy: getters.examToEdit.exIsStudy,
  }).catch(error => {
    commit("displayStatus", { message: "Error saving exam teaching case data.", level: "error" });
    return Promise.reject(error.response);
  });
};
export const editExamImagesInfo = ({getters, commit}) => {
  commit("updateExamImageDate", getters.examToEdit);
};
export const mergePatients = (context, ptSrNos) =>
  axios
    .post("/api/patient/merge", ptSrNos)
    .catch(error => Promise.reject(error.response));
// #endregion

// #region image browser
export const openBrowserForImage = ({ dispatch, commit, getters }, image) => {
  if (getters.loadingExams) {
    commit("displayStatus", { message: "Please wait for all exam data to load, then try again.", level: "error" });
    return;
  }

  if (image.imgType == "VIDEO") {
    let imgData = image.imgData;
    if (imgData) {
      if (imgData.endsWith("wmv")) {
        commit("displayStatus", { message: "The format of this exam (wmv) is no longer supported. Please re-upload the exam in an mp4 or avi format.", level: "error" });
        return;
      }
    }
    dispatch("openVideoViewer", image);
  }
  else {
    if (image.imgType == "IMAGE-SS" || image.imgType == "IMAGE-ST") {
      commit("displayStatus", { message: "The format of this exam (wmv) is no longer supported. Please re-upload the exam in an mp4 or avi format.", level: "error" });
      return;
    }
    else dispatch("openImageBrowser", image.imgKey);
    if (getters.isIE) router.push("imageBrowserTest");
  }
  return axios.post("/api/exam/log/" + image.exSrNo);
};
export const openImageBrowser = ({ dispatch }, imgKey) => {
  return dispatch("setBrowserContext", generateContextForImage(imgKey, false));
};
export const setBrowserContext = ({ commit, getters }, context) => {
  if (context) {
    if (getters.imageBrowserContext) getters.imageBrowserContext.clearContext();
    commit("setBrowserContext", context);
    commit("addToBrowserStack", context);
    if (getters.imageBrowserContext && getters.imageBrowserContext.startupTask)
      getters.imageBrowserContext.startupTask();
    if (getters.imageBrowserContext && getters.imageBrowserContext.reloadChildrenContext) {
      getters.imageBrowserContext.reloadChildrenContext();
    }
  }
  else
   commit("displayStatus", { message: "This exam can't be displayed yet.", level: "error" });
};
export const closeImageBrowser = ({ commit, dispatch }) => {
  commit("clearBrowserContext");
  dispatch("popBrowserStack");
};

// #region selectImages
export const selectNextImage = ({ getters, dispatch }) => {
  let start = getters.imageIndex;
  let next = Math.min(getters.imageIndex + 1, getters.browserImages.length - 1);
  if (!getters.browserImages[next].imgType.includes("IMAGE") && !VALID_IMG_TYPES.includes(getters.browserImages[next].imgType)) {
    while (!getters.browserImages[next].imgType.includes("IMAGE") && !VALID_IMG_TYPES.includes(getters.browserImages[next].imgType)) {
      next += 1;
      if (next >= getters.browserImages.length) next = 0;
      if (next == start) break;
    }
  }
  dispatch("selectImageByIndex", next);
};
export const selectPreviousImage = ({ getters, dispatch }) => {
  let start = getters.imageIndex;
  let next = Math.max(getters.imageIndex - 1, 0);
  if (!getters.browserImages[next].imgType.includes("IMAGE") && !VALID_IMG_TYPES.includes(getters.browserImages[next].imgType)) {
    while (!getters.browserImages[next].imgType.includes("IMAGE") && !VALID_IMG_TYPES.includes(getters.browserImages[next].imgType)) {
      next -= 1;
      if (next < 0) next = getters.browserImages.length - 1;
      if (next == start) break;
    }
  }
  dispatch("selectImageByIndex", next);
};
export const playImages = ({ getters, dispatch, commit }) => {
  if (!getters.imageBrowserContext) return;
  let intervalId = setInterval(
    () => dispatch("playNextImage"),
    1000 * Number(getters.playRate)
  );
  commit("setPlayInterval", intervalId);
};
export const playNextImage = ({ getters, dispatch }) => {
  let start = getters.imageIndex;
  let next = getters.imageIndex + 1;
  if (next > getters.browserImages.length - 1)
    next = 0;
  if (!getters.browserImages[next].imgType.includes("IMAGE") && !VALID_IMG_TYPES.includes(getters.browserImages[next].imgType)) {
    while (!getters.browserImages[next].imgType.includes("IMAGE") && !VALID_IMG_TYPES.includes(getters.browserImages[next].imgType)) {
      next += 1;
      if (next >= getters.browserImages.length) next = 0;
      if (next == start) break;
    }
  }
  if (next >= getters.browserImages.length) next = 0;
  dispatch("selectImageByIndex", next);
};
export const selectImageByIndex = ({ getters, commit, dispatch }, index) => {
  let image = getters.browserImages[index];
  if (getters.imageIndex !== index) {
    if ((VALID_IMG_TYPES.includes(image.imgType)) && getters.imageBrowserContext)
      commit("selectImage", image);
    else {
      if (image.imgData && image.imgData.endsWith("wmv") || image.imgType == "IMAGE-SS" || image.imgType == "IMAGE-ST") {
        commit("displayStatus", { message: "The format of this exam (wmv) is no longer supported. Please re-upload the exam in an mp4 or avi format.", level: "error" });
        return;
      }
      dispatch("openVideoViewer", image);
      dispatch("closeImageBrowser");
    }
  }
};
export const selectImageFromImageDrawer = ({ getters, commit, dispatch }, index) => {
  if (getters.browserImages.length < 1) {
    let images = getters.imagesByExSrNo(getters.videoExSrNo);
    let image = images[index];
    if (image.imgData && image.imgData.endsWith("wmv") || image.imgType == "IMAGE-SS" || image.imgType == "IMAGE-ST") {
      commit("displayStatus", { message: "The format of this exam (wmv) is no longer supported. Please re-upload the exam in an mp4 or avi format.", level: "error" });
      return null;
    }
    if (VALID_IMG_TYPES.includes(image.imgType)) {
      dispatch("openImageBrowser", image.imgKey);
      commit("clearVideoViewer");
    }
    else {
      dispatch("openVideoViewer", image);
      dispatch("closeImageBrowser");
    }
  }
  else {
    dispatch("selectImageByIndex", index);
  }
}

// #endregion

// #region color correction
export const toggleGrayscaleMode = ({ commit, dispatch, getters }, mode) => {
  commit("flattenedImageReady", false);
  if (getters.rgbView) {
    dispatch("selectRGBViewByMode", mode);
  } else {
    commit("toggleGrayscaleMode", mode);
  }
};
export const clearGrayscaleMode = ({ commit, dispatch, getters }) => {
  commit("flattenedImageReady", false);
  if (getters.rgbView) {
    dispatch("selectRGBViewByMode", GRAYSCALE_MODES.None);
  } else {
    commit("clearGrayscaleMode");
  }
};
export const updateBrightness = ({ commit, dispatch, getters }, brightness) => {
  commit("flattenedImageReady", false);
  commit("updateBrightness", brightness);
  if (getters.rgbView) dispatch("updateRGBSync");
};
export const updateContrast = ({ commit, dispatch, getters }, contrast) => {
  commit("flattenedImageReady", false);
  commit("updateContrast", contrast);
  if (getters.rgbView) dispatch("updateRGBSync");
};
export const updateGamma = ({ commit, dispatch, getters }, gamma) => {
  commit("flattenedImageReady", false);
  commit("updateGamma", gamma);
  if (getters.rgbView) dispatch("updateRGBSync");
};
export const updateGreenBrightness = ({ commit, dispatch, getters }, brightness) => {
  commit("flattenedImageReady", false);
  commit("updateGreenBrightness", brightness);
  if (getters.rgbView) dispatch("updateRGBSync");
};
export const updateGreenContrast = ({ commit, dispatch, getters }, contrast) => {
  commit("flattenedImageReady", false);
  commit("updateGreenContrast", contrast);
  if (getters.rgbView) dispatch("updateRGBSync");
};
// #endregion

// #region transformations
export const toggleFlipped = ({ commit, getters }) => {
  commit("clearAnnotationMode");
  commit("clearActiveAnnotation");
  commit("flattenedImageReady", false);
  commit("toggleFlipped");

  if (getters.rgbView)
    if (getters.rgbSync) commit("replicateMainToAllRGB");
    else commit("replicateMainToCurrentRGB");
};

export const toggleFlipped90 = ({ commit, getters }) => {
  commit("clearAnnotationMode");
  commit("clearActiveAnnotation");
  commit("flattenedImageReady", false);
  commit("toggleFlipped90");

  if (getters.rgbView)
    if (getters.rgbSync) commit("replicateMainToAllRGB");
    else commit("replicateMainToCurrentRGB");
};
// #endregion

// #region annotations
export const mousedownAnnotation = (
  { commit, getters, dispatch },
  eventWrapper
) => {
  commit("flattenedImageReady", false);
  if (getters.activeIsContinuous) {
    getters.activeAnnotation.mousedown(eventWrapper.x, eventWrapper.y, eventWrapper.e);
  } else {
    let previousActive = getters.activeAnnotation;
    commit("clearActiveAnnotation");
    let annotation = null;
    if (getters.inAddMode)
      annotation = createAnnotation(commit, getters, eventWrapper);
    else if (getters.inEditMode || getters.inShapeFillMode)
      annotation = getters.annotationByPosition(eventWrapper);
    if (getters.isDragLock && getters.inEditMode) 
    {
      if (annotation == previousActive)
        commit("setActiveAnnotation", annotation);
    }
    else
      commit("setActiveAnnotation", annotation);
    if (annotation) {
      if (getters.inDeleteMode) dispatch("deleteActiveAnnotation");
      else if (getters.isDragLock && getters.inEditMode) 
      {
        if (annotation == getters.activeAnnotation)
          annotation.mousedown(eventWrapper.x, eventWrapper.y, eventWrapper.e);
      }
      else {
        if (getters.inAddMode && annotation.onCreate) annotation.onCreate();
        annotation.mousedown(eventWrapper.x, eventWrapper.y, eventWrapper.e);
      }
    }
  }
};
function createAnnotation(commit, getters, position) {
  let type = getAnnotationType(getters.annotationMode);
  let annotation = type && new type(position, getters.annotationOptions, getters.imageBrowserContext);
  if (annotation) {
    annotation.setParentTransform(getters.imageTransform);
    commit("addAnnotation", annotation);
  }
  return annotation;
}
export const deleteActiveAnnotation = ({ commit, getters }) => {
  let annotation = getters.activeAnnotation;
  if (!annotation.delete()) return;
  commit("flattenedImageReady", false);
  annotation.deactivate();
  commit("clearActiveAnnotation");
  commit("deleteAnnotation", getters.annotationIndex(annotation));
};
// #endregion

// #region image flattening
export const generateFlatImage = ({ commit, getters }) => new Promise(resolve => {
  commit("clearActiveAnnotation");
  let img = getters.imgObject;
  let settings = getters.imageColorSettings;

  let canvas = getters.saveCanvas;
  canvas.width = img.width;
  canvas.height = img.height;
  let renderer = new CanvasRenderer(canvas);
  renderer.doScaleToPixelRatio = false;
  renderer.canvasSize = getters.imgSize;
  renderer.imgSize = getters.imgSize;
  renderer.isFlipped = getters.isFlipped;
  if (hasCustomPixelColorSettings(settings)) {
    // note: this method puts image data directly onto the canvas
    drawShadedImage(img, canvas, applyPixelShadingRules, settings);
  } else {
    renderer.drawImg(img);
  }
  // note: using "render" instead of "draw" here because draw erases the canvas!
  let ctx = canvas.getContext("2d");
  getters.annotations.forEach(a => renderer.renderAnnotation(a, ctx));

  commit("flattenedImageReady", true);
  resolve();
});
// #endregion

// #region rgb
export const toggleRGB = ({ commit, getters }) => {
  commit("toggleRGB");
  if (getters.rgbView) commit("replicateMainToAllRGB");
};
export const toggleRGBSync = ({ commit, dispatch, getters }) => {
  commit("toggleRGBSync");
  if (getters.rgbSync) dispatch("updateRGBSync");
};
export const toggleGreenSync = ({ commit, dispatch, getters }) => {
  commit("toggleGreenSync");
  if (getters.greenSync) dispatch("updateRGBSync");
};
export const selectRGBView = ({ commit, getters }, index) => {
  if (getters.currentRGBView && getters.currentRGBIndex !== index) {
    commit("flattenedImageReady", false);
    commit("selectRGBView", index);
    commit("replicateCurrentRGBToMain");
  }
};
export const selectRGBViewByMode = ({ dispatch, getters }, mode) => {
  let index = getters.rgbIndexByMode(mode);
  if (index === getters.currentRGBIndex) index = 0;
  dispatch("selectRGBView", index);
};
export const updateRGBSync = ({ commit, getters }) => {
  commit("replicateMainToCurrentRGB");
  if (getters.rgbSync) commit("replicateMainToAllRGB");
  if (getters.greenSync) commit("replicateGreenMainToAllRGB");
};
// #endregion

// #endregion

// #region oct browser

// #region oct images
export const getOCTInfo = (context, request) =>
  axios.post("api/image/octInfo", request);
export const loadOCTImages = ({ dispatch, getters }) =>
  dispatch("loadOCTQueues", getters.octImagesForCurrentBrowser);
export const loadOCTImagesForMultiview = ({ dispatch, getters }) =>
  dispatch("loadOCTQueues", getters.getOCTImagesForMultiview);
export const loadOCTQueues = ({ commit, dispatch }, images) => {
  commit("purgeOldOCTImages", images);
  let queues = constructOCTQueues(images);
  queues.map(queue => {
    commit("clearImageQueue", queue.key);
    commit("setImageQueue", { queue, queueKey: queue.key });
  });
  let promises = queues.map(queue => dispatch("loadQueue", queue.key));
  return Promise.all(promises);
};
function constructOCTQueues(images) {
  let fullLine = { next: 0, queue: [], concurrent: 5, cancelled: false, action: "loadOCTImage", key: "octFullQueue" };
  let segmentationLine = { next: 0, queue: [], concurrent: 5, cancelled: false, action: "loadOCTSegmentation", key: "octSegmentationQueue" };
  images.forEach(image => {
    if (!imageAlreadyLoaded(image, "img"))
      fullLine.queue.push({ image, prefix: "img" });
    if (image.loadSegmentation && !imageAlreadyLoaded(image, "segmentation"))
      segmentationLine.queue.push(image);
  });
  return [segmentationLine, fullLine];
}
export const loadOCTImage = ({ dispatch }, { image, prefix }) => {
  let request = generateOCTLoadRequest(image, prefix), action = "getOCTImage", mutation = "setOCTImageState";
  return dispatch("doLoadImage", { request, action, mutation, image, prefix });
};
export const loadOCTSegmentation = ({ commit, dispatch }, image) => {
  // if this image was already loaded, don't try to load it again
  if (imageAlreadyLoaded(image, "segmentation")) return Promise.resolve();
  let imageState = {
    segmentationIsLoading: true,
    segmentationIsQueued: false,
    imgKey: image.imgKey
  };
  commit("setOCTImageState", imageState);
  let loadRequest = generateOCTLoadRequest(image, "segmentation");
  return dispatch("getOCTImage", loadRequest)
    .then(response => processZipFile(response.data))
    .then(data => {
      imageState.sliceSegmentationLayerData = data.layerData;
      processSegmentationData(imageState);
    })
    .catch(() => {
      imageState.segmentationNotFound = true;
    })
    .finally(() => {
      imageState.segmentationIsLoading = false;
      commit("setOCTImageState", imageState);
    });
};
function generateOCTLoadRequest(image, prefix) {
  let request = {
    ptSrNo: image.ptSrNo,
    exSrNo: image.exSrNo,
    groupName: image.groupName,
    fileName: image.fileName,
    imageType: image.imageType,
    imageFormat: image.imageFormat,
    index: image.index,
    originalIndex: image.originalIndex,
    width: image.width,
    height: image.height
  };

  if (prefix === "preview") {
    request.isPreview = true;
    request.width = Math.min(200, image.width || 200);
    request.height = Math.min(200, image.height || 200);
  } else if (prefix === "segmentation") {
    request.imageType = image.segmentationImageType || image.imageType;
  } else if (prefix === "cscan") {
    request.imageType = "cscan";
  }
  return request;
}
function processZipFile(data) {
  return JSZip.loadAsync(data, { base64: true })
    .then(generateLayerPromises)
    .then(layerData => Promise.resolve({ src: data, layerData }));
}
function generateLayerPromises(zip) {
  let promises = [];
  for (var key in SliceLayerTypes) {
    let promise = zip.files[key] ? loadZipFile(zip, key)
      : Promise.resolve(new Float64Array(0));
    promises.push(promise);
  }
  return Promise.all(promises);
}
function loadZipFile(zip, key) {
  return zip.files[key].async("arraybuffer")
    .then(buf => Promise.resolve(new Float64Array(buf)));
}
function processSegmentationData(imageState) {
  let layerData = imageState.sliceSegmentationLayerData;
  if (!layerData || !layerData.length) return;
  let ilm = layerData[SliceLayerTypes.Ilm];
  let rpe = layerData[SliceLayerTypes.Rpe];
  if (!ilm || !rpe) return;
  let thicknessData = new Float64Array(ilm.length);
  for (let i = 0; i < Math.min(ilm.length, rpe.length); i++) {
    if (ilm[i] > 0 && rpe[i] > 0) thicknessData[i] = Math.abs(ilm[i] - rpe[i]);
  }
  imageState.sliceSegmentationThicknessData = thicknessData;
}
export const getOCTImage = (context, payload) =>
  axios.get("/api/image/octImage", { params: payload })
    .catch(error => Promise.reject(error.response));
export const loadOCTImageImmediately = ({ dispatch }, { image, key, view }) => {
  let uniqueKey = [key, view].filter(w => w).join("_");
  return debouncedLoadOCTImageCall(dispatch, image, uniqueKey);
};
// a unique key is passed into the debounced call which will be used within the debounce utility
// this key differentiates between compare_slice and baseline_slice, for example, so that both
// are debounced separately
const debouncedLoadOCTImageCall = debouncePerKey(function (dispatch, image) { // , uniqueKey
  let loadPreview = image.loadPreview ? dispatch("loadOCTImage", { image, prefix: "preview" })
    : Promise.resolve();
  loadPreview.then(() => dispatch("loadOCTImage", { image, prefix: "img" }));
}, 50, false, 2);
// #endregion

// #region composite
export const selectNextComposite =({ getters, dispatch }) => {
  let next = 0;
  if (getters.compositeCurrentIndex + 1 <= getters.composites.length - 1)
    next = getters.compositeCurrentIndex + 1;
  dispatch("selectOCTImageByIndex", {keyword: "composite", index: next});
};

export const selectPreviouscomposite = ({ getters, dispatch }) => {
  let next = getters.composites.length - 1;
  if (getters.compositeCurrentIndex - 1 >= 0)
    next = getters.compositeCurrentIndex - 1;
  dispatch("selectOCTImageByIndex", {keyword: "composite", index: next})
};
// #endregion

// #region oct slices
export const selectNextSlice = ({ getters, dispatch }) => {
  let next = 0;
  if (getters.sliceCurrentIndex + 1 <= getters.slices.length - 1)
    next = getters.sliceCurrentIndex + 1;
  dispatch("selectSliceByIndex", next);
};
export const selectPreviousSlice = ({ getters, dispatch }) => {
  let next = getters.slices.length - 1;
  if (getters.sliceCurrentIndex - 1 >= 0)
    next = getters.sliceCurrentIndex - 1;
  dispatch("selectSliceByIndex", next);
};
export const playSlices = ({ getters, dispatch, commit }) => {
  if (!getters.imageBrowserContext) return;
  let intervalId = setInterval(
    () => dispatch("playNextSlice"),
    1000 * Number(getters.playRate)
  );
  commit("setPlayInterval", intervalId);
};
export const playNextSlice = ({ getters, dispatch }) => {
  let next = getters.sliceCurrentIndex + 1;
  if (next >= getters.slices.length) next = 0;
  dispatch("selectSliceByIndex", next);
};
export const selectSliceByIndex = ({ commit, getters, dispatch }, index) => {
  if (getters.sliceCurrentIndex !== index) {
    let measureIndex = getters.isMeasuring ? getters.measureData.index : 0;
    let image = getters.slices.find(i => i.index === index);
    if (!image) return;
    // for now, the only images with playback are slices
    dispatch("selectOCTImage", { imgKey: image.imgKey, keyword: "slice" }).then(
      () => commit("setMeasurementIndex", measureIndex)
    );
  }
};
export const selectOCTImageByIndex = (
  { getters, dispatch, commit },
  { keyword, index }
) => {
  let collectionKey = keyword + "s";
  let collection = getters[collectionKey] || [];
  let image = collection.find(v => v.index === index);
  if (!image) return;
  dispatch("selectOCTImage", { keyword, imgKey: image.imgKey })
    .then(() => {
      if (keyword == "slice") {
        let measureIndex = getters.isMeasuring ? getters.measureData.index : 0;
        commit("setMeasurementIndex", measureIndex);
      }
    });

};
export const selectOCTImage = ({ commit }, { imgKey, keyword }) => {
  let currentKey = keyword + "Current";
  commit("selectOCTImage", { currentKey, imgKey: imgKey });
};
// #endregion

// #region oct exam/series/eye
export const selectOCTExam = ({ commit, dispatch, getters }, exSrNo) => {
  if (exSrNo === getters.exSrNo) return;
  let context = generateContextFromExam(exSrNo);
  if (context) return dispatch("setBrowserContext", context);

  commit("displayStatus", { message: "No OCT data can be displayed for this exam.", level: "error" });
};
export const selectOCTExamByExamText = ({ commit, dispatch, getters }, examText) => {
  let exam = getters.octExamsWithScanTypes.filter(e => e.octDateButtonText == examText);
  if (exam.length > 0)  {
    let groupkey = getters.currentEye === "OD" ? "odGroups" : "osGroups", group = "";
    exam[0][groupkey].forEach(e => {
      if (e.browserType != 1) {   
        if (exam[0].dateDisplay + " - " + e.eye + " " + e.groupSeriesType + " (" + exam[0].exSrNo + ")" == examText)
          group = e.groupName;
      }
      else {
        if (exam[0].dateDisplay + " - " + e.groupSeriesType + " (" + exam[0].exSrNo + ")" == examText)
          group = e.groupName;
      }
    })
    let context = generateContextFromGroupAndExSrNo(group, exam[0].exSrNo);
    if (context) return dispatch("setBrowserContext", context);
  }

  commit("displayStatus", { message: "No OCT data can be displayed for this exam.", level: "error" });
};
export const selectOCTGroup = ({ commit, dispatch, getters }, group) => {
  if (group === getters.currentGroup) return;
  let context = generateContextFromGroupName(group);
  if (context) return dispatch("setBrowserContext", context);

  commit("displayStatus", { message: "No OCT data can be displayed for this group.", level: "error" });
};
export const selectOCTEye = ({ commit, dispatch, getters }, eye) => {
  if (eye === getters.currentEye) return;
  let context = generateContextFromEye(eye);
  if (context) return dispatch("setBrowserContext", context);

  commit("displayStatus", { message: "No OCT data can be displayed for this eye.", level: "error" });
};
// #endregion

// #region oct color correction
export const toggleInverted = ({ commit }) => {
  commit("toggleInverted");
};
export const toggleColorPalette = ({ commit }) => {
  commit("toggleColorPalette");
};
// #endregion

// #region oct multiview
export const openOCTMultiviewFromThumbnail = ({ dispatch, getters, commit }, { image, launchOption }) => {
  let context = generateContextForImage(image.imgKey, false);
  if (context) {
    if (getters.imageBrowserContext) getters.imageBrowserContext.clearContext();
    commit("setBrowserContext", context);
    return dispatch("toggleOCTMultiview", launchOption);
  }
  else {
    commit("displayStatus", { message: "This exam can't be displayed yet.", level: "error" });
  }
};

export const toggleOCTMultiview = ({ dispatch, getters }, launchOption) => {
  if (!getters.imageBrowserContext) return;
  let context = null;
  if (getters.currentOCTBrowserType && !getters.isComparisonContext)
    context = generateMultiviewFromContext(getters.imageBrowserContext, launchOption);
  else if (getters.isComparisonContext)
    context = generateOCTContextFromGroup(getters.compare.group, getters.compare.exam);

  return dispatch("setBrowserContext", context);
};
export const selectOCTExamCompare = ({ getters }, { exSrNo, name }) => {
  if (!getters.isComparisonContext) return;
  let context = getters.imageBrowserContext;
  let groupName = name === "compare" ? context.compare.group.groupSeriesType : context.baseline.group.groupSeriesType.split('-')[0];
  let exam = getters.octExamsSameType.find(e => e.exSrNo === exSrNo && e.octDateButtonText.includes(groupName));
  let group = getFirstRelevantGroup(getters, exam);
  return selectOCTCompareView(context, name, group, exam);
};
export const selectOCTGroupCompare = ({ getters }, { groupName, name }) => {
  if (!getters.isComparisonContext) return;
  let context = getters.imageBrowserContext;
  let exam = name === "compare" ? context.compare.exam : context.baseline.exam;
  let groups =
    getters.currentEye === EyeTypes.OD ? exam.odGroups : exam.osGroups;
  let group = groups.filter(g => g.groupName === groupName)[0];
  return selectOCTCompareView(context, name, group, exam);
};

export const selectOCTExamCompareWithDropdown = ({ getters }, flSrNo) => {
  if (!getters.isComparisonContext) return;
  let context = getters.imageBrowserContext;
  let image = getters.imageByFlSrNo(flSrNo);
  let exam = getters.examBySrNo(image.exSrNo);
  if (exam)  {
    let groups = image.imgEye === EyeTypes.OD ? exam.odGroups : exam.osGroups;
    let group = groups.find(g => g.recognized && g.groupName === image.imgGroup);
    return selectOCTCompareView(context, "compare", group, exam, image.imgKey);
  }
  return selectOCTCompareView(context, "", null, null);
};

export const selectOCTExamCompareWithExamText = ({ getters }, { examText, name }) => {
  if (!getters.isComparisonContext) return;
  let context = getters.imageBrowserContext;
  let exam = context.exams.filter(e => e.octDateButtonText == examText);
  if (exam.length > 0)  {
    let group = exam[0].odGroups.length > 0 && examText.toLowerCase().includes("od") ? exam[0].odGroups[0] : exam[0].osGroups[0];
    if(exam[0].odGroups.length > 1 && examText.toLowerCase().includes("od") && examText.toLowerCase().includes("cube") && group.scanType != "cube"){
      group = exam[0].odGroups[1];
    }
    return selectOCTCompareView(context, name, group, exam[0]);
  }
  return selectOCTCompareView(context, name, null, null);
};
export const selectSpectralisGroupCompare = ({ getters }, { groupName, name }) => {
  if (!getters.isComparisonContext) return;
  let context = getters.imageBrowserContext;
  if (!context.baseline && name === "baseline") {
    if (getters.baselineExams.length > 0)
      return selectOCTCompareView(context, name, groupName, getters.baselineExams[0]);
    return selectOCTCompareView(context, name, groupName, null);
  }
  let exam = name === "compare" ? context.compare.exam : context.baseline.exam;
  let groups =
    getters.currentEye === EyeTypes.OD ? exam.odGroups : exam.osGroups;
  let group = [];
  if (groupName != "All") {
    if (groupName == "Radial")
      group = groups.filter(g => g.scanType.toLowerCase() === "star")[0];
    else
      group = groups.filter(g => g.scanType.toLowerCase() === groupName.toLowerCase())[0];
  }
    
  else
    group = name === "compare" ? context.compare.group : context.baseline.group ;
  return selectOCTCompareView(context, name, group, exam);
};
function selectOCTCompareView(context, name, group, exam, imgKey) {
  if (name === "compare") return context.selectCompare(group, exam, imgKey);
  else return context.selectBaseline(group, exam);
}
export const selectOCTEyeCompare = ({ commit, dispatch, getters }, eye) => {
  if (!getters.isComparisonContext) return;
  if (eye === getters.currentEye) return;
  let context = generateMultiviewFromEye(getters.imageBrowserContext, eye);
  if (context) {
    if (context.componentName == "spectralisComparisonBody") {
      let scanFilter = getters.spectralisScanTypeFilter;
      context.updateScanTypeFilter(scanFilter);
    }
    return dispatch("setBrowserContext", context);
  }

  commit("displayStatus", { message: "No OCT data can be displayed for this eye.", level: "error" });
};
export const tryGetBaselineContext = ({ getters }, { launchOption }) => {
  // if a context isn't open, or isn't a comparison context, or if the current baseline exam is legal,
  // then don't bother selecting a new baseline exam
  if (!getters.imageBrowserContext || !getters.isComparisonContext
    || getters.baselineExams.includes(getters.baseline_exam)) return Promise.resolve();

  let baselineExam = null;
  if (launchOption == THUMBNAIL_DROPDOWN.currentBaseline) {
    // exams are order by exDateTime DESC, the last one will be the oldest
    let sameTypeExams = getters.baselineExams.filter(e => e.scanType == getters.compare.group.scanType);
    if (sameTypeExams.length > 0)
      baselineExam = sameTypeExams[sameTypeExams.length - 1];
    else 
    {
      let oldestExamIndex = getters.baselineExams.length > 1 ? getters.baselineExams.length - 1 : 0;
      baselineExam = (getters.baselineExams || [])[oldestExamIndex];
    }
  }
  else {
    // exams are order by exDateTime DESC, the last one will be the oldest. For compare + previous, we want the most recent exam of the same type, so the first one
    let sameTypeExams = getters.baselineExams.filter(e => e.scanType == getters.compare.group.scanType);
    if (sameTypeExams.length > 0)
      baselineExam = sameTypeExams[0];
    else
      baselineExam = getters.baselineExams[0];
  }

  if (!baselineExam) {
    let group = getFirstRelevantGroup(getters, (getters.compareExams || [])[0]);
    return getters.imageBrowserContext.selectBaseline(group, null);
  }

  let group = getFirstRelevantGroup(getters, baselineExam);
  return getters.imageBrowserContext.selectBaseline(group, baselineExam);
};
function getFirstRelevantGroup(getters, exam) {
  if (!exam) return null;
  return getters.groups(exam, getters.currentEye, getters.currentOCTBrowserType, getters.preferredBrowserType)[0];
}
export const toggleLocked = ({ commit, getters, dispatch }) => {
  if (!getters.isComparisonContext) return;
  commit("toggleLocked");
  if (getters.isLocked) return dispatch("synchronizeOCTCompare");
};
export const synchronizeOCTCompare = ({ dispatch, getters }) => {
  let compare = getters.compare;
  let baseline = getters.baseline;
  if (!baseline) return;

  if (baseline.playRate !== compare.playRate)
    baseline.setPlayRate(compare.playRate);

  if (baseline.isMeasuring !== compare.isMeasuring)
    baseline.toggleMeasuring && baseline.toggleMeasuring();

  if (baseline.measureData && baseline.measureData.index !== compare.measureData.index)
    baseline.setMeasurementIndex && baseline.setMeasurementIndex(compare.measureData.index);

  if (baseline.showSegmentation !== compare.showSegmentation)
    baseline.toggleSegmentation && baseline.toggleSegmentation();

  if (baseline.showETDRS !== compare.showETDRS)
    baseline.toggleETDRS && baseline.toggleETDRS();

  if (baseline.showOverlay !== compare.showOverlay)
    baseline.toggleOverlay && baseline.toggleOverlay();

  if (baseline.overlayOpacity !== compare.overlayOpacity)
    baseline.updateOverlayOpacity && baseline.updateOverlayOpacity(compare.overlayOpacity);

  if (baseline.octColorSettings.isInverted !== compare.octColorSettings.isInverted)
    baseline.toggleInverted && baseline.toggleInverted();
  // following if statement:
  //  need to toggle color palette if one is null and the other is not
  //  don't need to toggle if both are null, or if both are not null
  //  colorPalette is an array
  if ((baseline.octColorSettings.colorPalette === null) !== (compare.octColorSettings.colorPalette === null))
    baseline.toggleColorPalette && baseline.toggleColorPalette();

  let compareIndex = compare.sliceCurrent && compare.sliceCurrent.index;
  let baselineIndex = baseline.sliceCurrent && baseline.sliceCurrent.index;
    
  if (compare.componentName == "spectralisComparisonView" && baseline.componentName == "spectralisComparisonView") {
    let compareSliceCount = getters.compare_slices.length;
    let baselineSliceCount =  getters.baseline_slices.length;
    
    if ((compareIndex / compareSliceCount) != (baselineIndex / baselineSliceCount)) {
        dispatch("selectSliceByIndexProportionCompare", { view: "baseline", index: parseInt(compareIndex / compareSliceCount * baselineSliceCount), synchronizing: true });
    }
  }

  else {
    if (compareIndex !== baselineIndex)
      dispatch("selectSliceByIndexCompare", { view: "baseline", index: compareIndex });
  }
};
// #endregion

// #region oct image selection
export const selectNextSliceCompare = ({ getters, dispatch }, view) => {
  let context = getters[view];
  if (!context) return;
  let slices = getters.getSlices("slice", context.exSrNo, context.groupName);
  let index = 0;
  if (context.sliceCurrent.index + 1 <= slices.length - 1)
    index = context.sliceCurrent.index + 1;

  if (context.componentName == "spectralisComparisonView")
    dispatch("selectSliceByIndexProportionCompare", { view, index });
  else
    dispatch("selectSliceByIndexCompare", { view, index });
};
export const selectPreviousSliceCompare = ({ getters, dispatch }, view) => {
  let context = getters[view];
  if (!context) return;
  let slices = getters.getSlices("slice", context.exSrNo, context.groupName);
  let index = slices.length - 1;
  if (context.sliceCurrent.index - 1 > 0)
    index = context.sliceCurrent.index - 1;
  
  if (context.componentName == "spectralisComparisonView")
    dispatch("selectSliceByIndexProportionCompare", { view, index });
  else
    dispatch("selectSliceByIndexCompare", { view, index });
};
export const playSlicesCompare = ({ commit, getters, dispatch }, view) => {
  let context = getters[view];
  if (!context) return;
  let intervalId = setInterval(() => dispatch("playNextSliceCompare", view), 1000 * Number(context.playRate));
  commit("setPlayIntervalCompare", { view, intervalId });
};
export const playNextSliceCompare = ({ getters, dispatch }, view) => {
  let context = getters[view];
  if (!context) return;
  let slices = getters.getSlices("slice", context.exSrNo, context.groupName);
  let index = context.sliceCurrent.index + 1;
  if (index >= slices.length) index = 0;

  if (context.componentName == "spectralisComparisonView")
    dispatch("selectSliceByIndexProportionCompare", { view, index });
  else
    dispatch("selectSliceByIndexCompare", { view, index });
};
export const selectSliceByIndexCompare = ({ getters, dispatch }, { index, view }) => {
  if (!getters.isComparisonContext) return;
  if ((getters.isLocked || view === "compare") && getters.compare)
    dispatch("selectSliceByIndexForView", { index, view: "compare" });
  if ((getters.isLocked || view === "baseline") && getters.baseline)
    dispatch("selectSliceByIndexForView", { index, view: "baseline" });
};

export const selectSliceByIndexProportionCompare = ({ getters, dispatch }, { index, view, synchronizing = false }) => {
  if (!getters.isComparisonContext) return;
  if (getters.isLocked && getters.compare && getters.baseline && !synchronizing) {
    let selected, toChange, toChangeView;
    if (view === "compare") {
      selected = getters.compare_slices.length;
      toChange = getters.baseline_slices.length;
      toChangeView = "baseline";
    }
    else if (view === "baseline") {
      selected = getters.baseline_slices.length;
      toChange = getters.compare_slices.length;
      toChangeView = "compare";
    }  

    dispatch("selectSliceByIndexForView", { index, view });
    let selectedProportion = index / selected;
    let toChangeProportion = parseInt(selectedProportion * toChange);
    dispatch("selectSliceByIndexForView", { index: toChangeProportion, view: toChangeView });
  }
  else {
    if ((view === "compare") && getters.compare)
      dispatch("selectSliceByIndexForView", { index, view: "compare" });
    if ((view === "baseline") && getters.baseline)
      dispatch("selectSliceByIndexForView", { index, view: "baseline" });
  }
};

export const selectSliceByIndexForView = ({ commit, dispatch, getters }, { view, index }) => {
  let context = getters[view];
  if (!context) return;
  if (context.sliceCurrent && context.sliceCurrent.index !== index) {
    let slices = getters.getSlices("slice", context.exSrNo, context.groupName);
    let hasMeasureData = context.isMeasuring && !!context.measureData;
    let measureIndex = hasMeasureData && context.measureData.index;
    let image = slices.find(i => i.index === index);
    if (!image) return;
    // for now, the only images with playback are slices
    dispatch("selectOCTImage", { imgKey: image.imgKey, keyword: "slice", view })
      .then(() => {
        if (hasMeasureData) commit("setMeasurementIndexCompare", { x: measureIndex, view });
      });
  }
};
export const selectOCTImageByIndexCompare = (
  { getters, dispatch, commit },
  { keyword, index, view }
) => {
  if (!getters.isComparisonContext) return;
  let context = getters[view];
  let hasMeasureData = context.isMeasuring && !!context.measureData;
  let measureIndex = hasMeasureData && context.measureData.index;
  if ((getters.isLocked || view === "compare") && getters.compare) 
    dispatch("selectOCTImageByIndexForView", { keyword, index, view: "compare" }).then(() => {
      if (hasMeasureData) commit("setMeasurementIndexCompare", { x: measureIndex, view });
    });
  if ((getters.isLocked || view === "baseline") && getters.baseline)
    dispatch("selectOCTImageByIndexForView", { keyword, index, view: "baseline" }).then(() => {
      if (hasMeasureData) commit("setMeasurementIndexCompare", { x: measureIndex, view });
    });
};

export const selectOCTImageByIndexForView = ({ dispatch, getters }, { keyword, view, index }) => {
  let context = getters[view];
  if (!context) return;
  let collection = getters.getSlices(
    keyword,
    context.exSrNo,
    context.groupName
  );
  let image = collection.find(v => v.index === index);
  if (!image) return;
  return dispatch("selectOCTImage", { keyword, imgKey: image.imgKey, view });
};

export const selectOCTImageByIndexProportionCompare = (
  { getters, dispatch, commit },
  { keyword, index, view }
) => {
  if (!getters.isComparisonContext) return;
  let context = getters[view];
  let hasMeasureData = context.isMeasuring && !!context.measureData;
  let measureIndex = hasMeasureData && context.measureData.index;
  if (getters.isLocked && getters.compare && getters.baseline) {
    let selected, toChange, toChangeView;
    if (view === "compare") {
      selected = getters.compare_slices.length;
      toChange = getters.baseline_slices.length;
      toChangeView = "baseline";
    }
    else if (view === "baseline") {
      selected = getters.baseline_slices.length;
      toChange = getters.compare_slices.length;
      toChangeView = "compare";
    }  

    dispatch("selectOCTImageByIndexForView", { keyword, index, view }).then(() => {
      if (hasMeasureData) commit("setMeasurementIndexCompare", { x: measureIndex, view });
    });

    let selectedProportion = index / selected;
    let toChangeProportion = parseInt(selectedProportion * toChange);
    dispatch("selectOCTImageByIndexForView", { keyword, view: toChangeView, index: toChangeProportion }).then(() => {
      if (hasMeasureData) commit("setMeasurementIndexCompare", { x: measureIndex, view });
    });
  }
  else {
    if ((view === "compare") && getters.compare)
      dispatch("selectOCTImageByIndexForView", { keyword, index, view: "compare" }).then(() => {
        if (hasMeasureData) commit("setMeasurementIndexCompare", { x: measureIndex, view });
      });
    if ((view === "baseline") && getters.baseline)
      dispatch("selectOCTImageByIndexForView", { keyword, index, view: "baseline" }).then(() => {
        if (hasMeasureData) commit("setMeasurementIndexCompare", { x: measureIndex, view });
      });
  }
};

export const toggleColorCompare = ({ commit }, view) => {
  commit("toggleColorCompare", view);
};
export const toggleInvertedCompare = ({ commit }, view) => {
  commit("toggleInvertedCompare", view);
};
// #endregion

// #endregion

// #region hvf
export const selectHVFExam = ({ commit, getters }, { exSrNo, index }) => {
  if (!getters.imageBrowserContext) return;
  commit("selectHVFExam", { exam: getters.examBySrNo(exSrNo), index });
};
export const openHVFMultiview = ({ dispatch, getters }) => {
  if (!getters.exam) return;
  let context = new HvfMultiviewContext(getters.exam);
  return dispatch("setBrowserContext", context);
};
export const closeHVFMultiview = ({ dispatch, getters }) => {
  if (!getters.exSrNo || !getters.exam) return;
  let image = getters.imagesByExSrNo(getters.exSrNo)[0];
  if (!image) return;
  let context = new HvfContext(getters.exam, image);
  return dispatch("setBrowserContext", context);
};
// #endregion

// #region stereo browser
export const openStereo = ({ commit, getters }, images) => {
  if (!images || !images.length) {
    commit("displayStatus", { message: "Please select at least one image.", level: "error" });
    return;
  }
  let exSrNo = images[0].exSrNo;
  let exImages = getters.imagesByExSrNo(exSrNo);
  if (images.some(i => i.exSrNo !== exSrNo)) {
    commit("displayStatus", { message: "Selected images must all be from a single exam.", level: "error" });
    return;
  } else if (exImages.length <= 1) {
    commit("displayStatus", { message: "Selected exam must have at least two images.", level: "error" });
    return;
  }

  let context = new StereoContext(images, exImages);
  commit("setStereoContext", context);
  commit("addToBrowserStack", context);

  context.refreshImages();
};
export const closeStereo = ({ commit, dispatch }) => {
  commit("clearStereoContext");
  dispatch("popBrowserStack");
};
export const setInitialStereoPair = ({ commit, getters }) => {
  let images = getters.selectedImages;
  if (images.length > 1)
    commit("setStereoPair", {
      image1: images[0],
      image2: images[1],
      index: 0
    });
};
export const changeSelectedStereoPair = ({ commit, getters }, button) => {
  let index = getters.stereoSelectedPair.index;
  if (button == "Next") {
    if (index < getters.stereoImagePairs.length - 1)
      commit("setStereoPair", getters.stereoImagePairs[index + 1]);
  }
  else if (button == "Previous") {
    if (index > 0)
      commit("setStereoPair", getters.stereoImagePairs[index - 1]);
  }
  else if (button == "First")
    commit("setStereoPair", getters.stereoImagePairs[0]);
  else {
    commit("setStereoPair", getters.stereoImagePairs[getters.stereoImagePairs.length - 1]);
  }
};
export const swapSelectedStereoPair = ({ commit, getters }) => {
  commit("setStereoPair", {
    image1: getters.stereoSelectedPair.image2,
    image2: getters.stereoSelectedPair.image1,
    index: getters.stereoSelectedPair.index
  });
};
// #endregion

// #region sideBySide
export const openSideBySide = ({ commit, dispatch }, images) => {
  if (!images || !images.length || images.length !== 2) {
    commit("displayStatus", { message: "Please select exactly 2 images for side by side view.", level: "error" });
    return;
  }

  let context = new SideBySideContext();
  commit("setSideBySideContext", context);
  commit("addToBrowserStack", context);

  Promise.all([
    dispatch("setSideBySideImageBrowserContext", { isLeft: true, image: images[0] }),
    dispatch("setSideBySideImageBrowserContext", { isLeft: false, image: images[1] })
  ]).then(() => dispatch("loadImagesForSideBySide"));
};
export const closeSideBySide = ({ commit, dispatch }) => {
  commit("clearSideBySideContext");
  dispatch("popBrowserStack");
};
export const setSideBySideImageBrowserContext = ({ commit }, { isLeft, image }) => {
  commit("setSideBySideImageBrowser", { isLeft, context: generateContextForImage(image.imgKey, true) });
  return Promise.resolve();
};
export const sideBySideSelectExam = ({ dispatch, getters }, { exSrNo, isLeft }) => {
  let image = getters.imagesByExSrNo(exSrNo)[0];
  dispatch("setSideBySideImageBrowserContext", { image, isLeft })
    .then(() => dispatch("loadImagesForSideBySide")); 
};
export const sideBySideSelectImage = ({ commit }, { image, isLeft }) => {
  commit("setSideBySideImageBrowserContextImage", { isLeft, image });
};
// #endregion

// #region overlay browser
export const openOverlay = ({ commit, dispatch, getters }, sourceImages) => {
  sourceImages = sourceImages.slice().sort((a, b) => {
    if (a.exSrNo == b.exSrNo)
      return a.exIndex - b.exIndex;
    else
      return b.exDate - a.exDate;
  });
  if (sourceImages.length < 1)
    return dispatch("displayErrorAndReject", "You must select at least one image for Overlay.");
  if (sourceImages.length > 1 && differentEyes(sourceImages))
    return dispatch("displayErrorAndReject", "You must select images from only one eye for Overlay.");

  // if there's only one image, it doesn't need to be registered
  if (sourceImages.length === 1)
    return dispatch("loadRegisteredImages", { sourceImages, registeredImages: sourceImages });

  // if we've already registered this set of images, use what we have
  let flSrNos = sourceImages.map(i => i.flSrNo).join("_");
  let registeredImages = getters.i2kImagesByFlSrNos(flSrNos);
  if (registeredImages && registeredImages.length)
    return dispatch("loadRegisteredImages", { sourceImages, registeredImages });

  commit("setLoadingMessage", "Registering images");
  let exSrNos = sourceImages.map(i => i.exSrNo);
  let requestData = mapI2kRequests(sourceImages);
  return axios.post("/api/i2k/register", {
    "images": requestData
  }).catch(() => dispatch("displayErrorAndReject", "An error occurred while registering images. Please try again later."))
    .then(response => {
      let registeredImages = response.data.map((path, index) => {
        return {
          path,
          imgKey: "register_" + flSrNos + "_" + index,
          flSrNos,
          exSrNos
        };
      });
      return dispatch("loadRegisteredImages", { sourceImages, registeredImages });
    }).finally(() => commit("setLoadingMessage", null));
};
function mapI2kRequests(images) {
  return images.map(i => {
    return {
      ptSrNo: i.ptSrNo,
      exSrNo: i.exSrNo,
      flSrNo: i.flSrNo,
      flName: i.flName,
      storeSrNo: i.storeSrNo
    };
  });
}
export const displayErrorAndReject = ({ commit }, message) => {
  commit("displayStatus", { message, level: "error" });
  return Promise.reject(message);
};
export const cancelAddImagesToOverlay = ({ dispatch, getters }) => {
  return dispatch("openOverlay", getters.previousOverlayImages);
};
export const loadRegisteredImages = ({ commit, dispatch, getters }, { sourceImages, registeredImages }) => {
  // if no appropriate images are selected, complain
  if (!registeredImages || !registeredImages.length
    || registeredImages.some(i => (i.path || "").includes("ERROR"))) {
    commit("displayStatus", { message: "i2k was unable to register the selected images.", level: "error" });
    return Promise.reject(registeredImages || "No images were registered");
  }

  if (registeredImages.length !== 1){
    var flSrNoList = registeredImages[0].flSrNos.split("_");
    for (var i = 0; i < registeredImages.length; i++) {
      var result = sourceImages.filter(obj => { return obj.flSrNo == flSrNoList[i]; })
      if (result.length > 0)
        registeredImages[i].exDateDisplay = result[0].exDateDisplay
    }
  }

  commit("setOriginalOverlaySettings", { sourceImages });
  dispatch("loadOverlayImages", registeredImages);

  commit("openOverlay", registeredImages);
  commit("addToBrowserStack", getters.overlayContext);
};
export const loadOverlayImages = ({ dispatch, commit }, images) => {
  if (!images || !images.length) return;
  if (images.length === 1) {
    // if there's only one image, then it hasn't been registered and can be loaded with the normal load method
    dispatch("loadImage", { image: images[0], thumb: false, proof: false, prefix: "img" });
  } else {
    // if there's more than one image, they have been registered and need to be loaded from the i2k endpoint
    commit("storeI2kImages", images);
    images.forEach(image => dispatch("loadI2kImage", image));
  }
};
export const closeOverlay = ({ commit, dispatch }) => {
  commit("setOriginalOverlaySettings", null);
  commit("clearOverlay");
  dispatch("popBrowserStack");
};
export const closeOverlayToAddImages = ({ commit, dispatch }) => {
  commit("clearOverlay");
  dispatch("popBrowserStack");
};
function differentEyes(images) {
  if (!images || images.length < 2) return false;
  let eye = images[0].imgEye;
  return images.some(i => i.imgEye !== eye);
}
// #endregion

// #region montage browser
export const openMontage = ({ commit, dispatch, getters }, images) => {
  images = images.slice().sort((a, b) => a.flSrNo - b.flSrNo);
  if (images.length < 2)
    return dispatch("displayErrorAndReject", "You must select at least two images for Montage.");

  // if we've already generated a montage for this set of images, use what we have
  let latestImage = images.reduce(function (prev, current) {
    return (moment(prev.exDate).isAfter(current.exDate)) ? prev : current
  });
  let flSrNos = images.map(i => i.flSrNo);
  let imgKey = "montage_" + flSrNos.join("_");
  let image = getters.i2kImageByKey(imgKey);
  let exam = getters.examBySrNo(images[0].exSrNo);
  let exSrNos = images.map(i => i.exSrNo);

  if (image) return dispatch("loadMontageImage", { image, exam });

  commit("setLoadingMessage", "Generating montage");
  let requestData = mapI2kRequests(images);
  return axios.post("/api/i2k/montage", {
    "images": requestData
  }).catch(() => dispatch("displayErrorAndReject", "A montage could not be produced from the selected images"))
    .then(response => dispatch("loadMontageImage", {
      image: {
        path: response.data,
        imgKey,
        exSrNos,
        flSrNos,
        ptSrNo: latestImage.ptSrNo,
        exSrNo: latestImage.exSrNo,
        imgEye: latestImage.imgEye,
        imgIndex: latestImage.imgIndex,
        flSrNo: latestImage.flSrNo,
        flName: latestImage.flName,
        imgType: latestImage.imgType,
        imgGroup: latestImage.imgGroup,
        rcssrNo: latestImage.rcssrNo,
        rcimgIntrimPath: latestImage.rcimgIntrimPath
      }, exam
    }))
    .finally(() => commit("setLoadingMessage", null));
};
export const loadMontageImage = ({ commit, dispatch }, { image, exam }) => {
  if (!image.path
    || image.path === "NO_IMAGES_ALIGNED"
    || image.path.includes("ERRORGENRATINGMONTAGE"))
    return dispatch("displayErrorAndReject", "A montage could not be produced from the selected images");

  commit("storeI2kImages", [image]);
  return dispatch("loadI2kImage", image).then(() => {
    let context = new ImageContext(image, [image], exam);
    context.showMeasurementSection = context.showPlaybackToolbar = context.showImageDrawer = false;
    return dispatch("setBrowserContext", context);
  }).catch(() => dispatch("displayErrorAndReject", "A montage could not be produced from the selected images"));
};
export const loadI2kImage = ({ dispatch }, image) => {
  let prefix = "img";
  let request = { path: image.path }, action = "getI2kImage", mutation = "setI2kImageState";
  return dispatch("doLoadImage", { request, action, mutation, image, prefix });
};
export const getI2kImage = (context, image) =>
  axios.get("/api/image/i2kImage", { params: { path: image.path } })
    .catch(error => Promise.reject(error.response));
// #endregion

// #region print preview
export const openPrintPreview = ({ commit, dispatch, getters }, images) => {
  if (!images || !images.length) {
    commit("displayStatus", { message: "Please select at least one image to print.", level: "error" });
    return;
  }
  if (getters.isOpenedInApp || window.isOpenedInApp) {
    let data = {
      access: getters.auth.access_token,
      images: images.map(i => i.flSrNo),
      ptSrNo: getters.ptSrNo,
      exSrNo: getters.exSrNo || images[0].exSrNo,
      ptId: getters.ptId,
      ptSex: getters.ptSex
    }
    requestPrintInApp(data);
  } else {
    let context = new PrintPreviewContext(images);
    commit("setPrintPreviewContext", context);
    commit("addToBrowserStack", context);
    dispatch("loadImages_full", images);
  }
};

export const closePrintPreview = ({ commit, dispatch }) => {
  commit("clearPrintPreviewContext");
  dispatch("popBrowserStack");
};

// #region app print
export const appPrintSetup = ({ commit, dispatch, getters }, query) => {
  let access = query.access;
  let imagesID = query.images.split(",");
  let exSrNo = [query.exSrNo];

  commit("setAxiosHeader", access);

  let patientSearch = {
    timespan: PATIENT_TIMESPAN.all,
    searchBy: SEARCH_BY.EMRLink,
    keyword: "",
    sortBy: ["ptId"],
    sortDesc: [false],
    groupBy: ["exDateIso"],
    groupDesc: [true],
    Page: 1,
    ItemsPerPage: 1,
    AdvancedSearch: {
      ExSrNo: query.exSrNo,
      ExamTypes: [],
      PatientName: "",
      PatientId: query.ptId,
      PtSex: query.ptSex
    }
  };

  return axios.post("/api/patient/list", patientSearch)
    .then(response => {
      if (response.data.patients.length > 0) {
        let value = response.data.patients[0];
        let patient = {
          ptId: value.ptID,
          ptSrNo: value.ptSrNo,
          exDate: value.exDate,
          exMoment: moment(value.exDate),
          exDateDisplay: formatDateDisplay(value.exDate),
          exDateIso: formatDateISO(value.exDate),
          lastName: value.nameLast,
          firstName: value.nameFirst,
          gender: value.gender,
          dob: formatDateDisplay(value.dob),
          isoDOB: formatDateISO(value.dob),
          status: value.ptValidityStatus + "",
          tableKey: value.ptSrNo + "_" + formatDateISO(value.exDate)
        };
        commit("selectPatient", patient);

        return dispatch("getPatientExams");
      } else {
        commit("displayStatus", { message: "Patient not found", level: "error" });
      }
    })
    .then(() => {
      return dispatch("loadExamImageMetadata", exSrNo);
    })
    .then(() => {
      let images = [];

      imagesID.forEach(value => {
        let image = getters.imageByKey(value);
        images.push(image);
      });

      return dispatch("openPrintPreview", images);
    })
    .catch(error => Promise.reject(error.response));
}
// #endregion
// #endregion

// #region browser stack
export const popBrowserStack = ({ commit, dispatch, getters }) => {
  logDetailed("actions", "popBrowserStack");
  commit("popBrowserStack");
  if (getters.browserStack.length) {
    // if a browser is open, refresh the top browser
    dispatch("refreshTopBrowser");
  } else {
    // if no browser is open, refresh the patients list
    logDetailed("actions", "popBrowserStack", "all browsers closed - refreshing patient list");
    // refresh the patients list but not entire exam list
    dispatch("getPatients", true);
  }
};
export const refreshTopBrowser = ({ dispatch, getters }) => {
  let browser = getters.topBrowser;
  if (browser && browser.refreshImages) browser.refreshImages();
  else dispatch("getThumbnailsForOpenExams");
};
export const setOpenedExams = ({ commit, dispatch }, exams) => {
  commit("setOpenedExams", exams);
  dispatch("getThumbnailsForOpenExams");
};
// #endregion

// #region video viewer 
export const openVideoViewer = ({ commit }, video) => {
  commit("openVideoViewer", video);
};
// #endregion

// #region admin reports
export const getChartResult = (context, chartCriteria) => {
  return axios.post("/api/report/chartResult", chartCriteria)
    .then(response => {
      return Promise.resolve(response);
    })
    .catch(error => Promise.reject(error.response));
}

export const submitExamSummaryReport = (context, examSummaryCriteria) => {
  let request = {
    searchBy: SEARCH_BY.Advanced,
    dateStart: examSummaryCriteria.dateFrom ? moment(examSummaryCriteria.dateFrom + "T00:00:00") : null,
    dateEnd: examSummaryCriteria.dateTo ? moment(examSummaryCriteria.dateTo + "T00:00:00") : null,
    advancedSearch: {
      ReportTechingCase: JSON.parse(examSummaryCriteria.teaching),
      ReportDictatedCase: JSON.parse(examSummaryCriteria.dictated),
      ExamTypes: examSummaryCriteria.device,
      ExamPhysicians: examSummaryCriteria.physician,
      ExamPathology: examSummaryCriteria.examPathology,
      ReportGroupBy: examSummaryCriteria.reportGroupBy,
      DevLocation: examSummaryCriteria.devLocations,
      DevType: examSummaryCriteria.devTypes
    },
    page: examSummaryCriteria.pageNumber,
    itemsPerPage: examSummaryCriteria.rowPerPage
  }
  return axios.post("/api/report/examSummary", request)
    .then(response => {
      return Promise.resolve(response);
    })
    .catch(error => Promise.reject(error.response));
}

export const submitUserActivityReport = (context, userActivityCriteria) => {
  let request = {
    UserSrNo: userActivityCriteria.usrSrNo,
    DateFrom: userActivityCriteria.dateFrom ? moment(userActivityCriteria.dateFrom + "T00:00:00") : null,
    DateTo: userActivityCriteria.dateTo ? moment(userActivityCriteria.dateTo + "T00:00:00") : null,
    Activity: userActivityCriteria.userActivities,
    PageNumber: userActivityCriteria.pageNumber,
    RowsOfPage: userActivityCriteria.rowPerPage
  }
  return axios.post("/api/report/userActivity", request)
    .then(response => {
      response.data.data.list.forEach(item => {
        item.hplTmStamp = moment(item.hplTmStamp).utc().format("YYYY-MM-DD HH:mm:ss") + " UTC";
        item.patientID = item.patientID == 0 ? "" : item.patientID;
        item.examID = item.examID == 0 ? "" : item.examID;
      });
      return Promise.resolve(response);
    })
    .catch(error => Promise.reject(error.response));
}

export const submitDeviceHistory = ( {getters}, deviceHistoryCriteria) => {
  let request = {
    Users: deviceHistoryCriteria.users,
    DateFrom: deviceHistoryCriteria.dateFrom ? moment(deviceHistoryCriteria.dateFrom + "T00:00:00") : null,
    DateTo: deviceHistoryCriteria.dateTo ? moment(deviceHistoryCriteria.dateTo + "T00:00:00") : null,
    Devices: deviceHistoryCriteria.devices,
    PageNumber: deviceHistoryCriteria.pageNumber,
    RowsOfPage: deviceHistoryCriteria.rowPerPage
  };
  return axios.post("/api/report/deviceHistory", request)
    .then(response => {
      response.data.data.list.forEach(item => {
        item.tmStamp = moment(item.tmStamp).utc().format("YYYY-MM-DD HH:mm:ss") + " UTC";
        item.changes = [];
        if (item.oldValues == "") 
        {
          let firstObject = {
            devProc: item.devProc,
            tmStamp: item.tmStamp, 
            username: getters.userList.filter(a => a.srNo == item.userSrNo)[0].name,
            field: "All",
            oldValues: "-",
            newValues: item.newValues,
          };
          item.changes.push(firstObject);
        }
        else {
          let oldJson = JSON.parse(item.oldValues);
          let newJson = JSON.parse(item.newValues);
          for (let key in oldJson) {
            if (DEVICE_HISTORY_EDITABLE_FIELDS.includes(key.toLowerCase()))  
            {
              if (oldJson[key] != newJson[key]) 
              {
                let diffObject = {
                  devProc: item.devProc,
                  tmStamp: item.tmStamp, 
                  username: getters.userList.filter(a => a.srNo == item.userSrNo)[0].name,
                  field: key,
                  oldValues: oldJson[key],
                  newValues: newJson[key],
                };
                item.changes.push(diffObject);
              }
            }
          }
        }
      });
      return Promise.resolve(response);
    })
    .catch(error => Promise.reject(error.response));
}
// #endregion
